import { Container, Sprite, type Texture } from 'pixi.js';

export interface EmitOptions {
  x: number;
  y: number;
  texture: Texture;
  /** Optional frame cycle (e.g. coin face/edge): the sprite steps through these every `frameMs`. */
  frames?: Texture[];
  frameMs?: number;
  count: number;
  /** Speed range in px/s (world units). */
  speed: [number, number];
  /** Emission direction in radians and half-spread; omit for all directions. */
  angle?: number;
  spread?: number;
  gravity?: number; // px/s²
  life: [number, number]; // ms
  scale?: [number, number];
  tint?: number | number[];
  alpha?: number;
  /** Fade out over the last fraction of life. */
  fade?: boolean;
  rotationSpeed?: number; // rad/s max (random sign)
  bounceY?: number; // world y at which particles bounce once (e.g. floor)
  bounceDamping?: number;
}

interface Particle {
  sprite: Sprite;
  vx: number;
  vy: number;
  vr: number;
  life: number;
  age: number;
  gravity: number;
  fade: boolean;
  bounceY: number | undefined;
  bounced: boolean;
  bounceDamping: number;
  frames: Texture[] | undefined;
  frameMs: number;
}

/** Pooled sprite particles with a hard global cap and a per-emit cap. */
export class ParticleSystem {
  readonly container = new Container();
  private live: Particle[] = [];
  private pool: Sprite[] = [];
  readonly maxParticles: number;
  readonly maxPerEmit: number;

  constructor(maxParticles = 400, maxPerEmit = 60) {
    this.maxParticles = maxParticles;
    this.maxPerEmit = maxPerEmit;
  }

  get count(): number {
    return this.live.length;
  }

  emit(o: EmitOptions): void {
    let n = Math.min(o.count, this.maxPerEmit, this.maxParticles - this.live.length);
    if (n <= 0) {
      // Recycle the oldest to keep the newest event visible.
      const recycle = Math.min(o.count, this.maxPerEmit, Math.floor(this.maxParticles * 0.25));
      for (let i = 0; i < recycle && this.live.length; i++) this.kill(this.live[0]);
      n = Math.min(o.count, this.maxPerEmit, this.maxParticles - this.live.length);
    }
    for (let i = 0; i < n; i++) {
      const s = this.pool.pop() ?? new Sprite();
      s.texture = o.texture;
      s.anchor.set(0.5);
      s.position.set(o.x, o.y);
      const sc = o.scale ? rand(o.scale[0], o.scale[1]) : 1;
      s.scale.set(sc);
      s.alpha = o.alpha ?? 1;
      s.rotation = Math.random() * Math.PI * 2;
      s.tint = Array.isArray(o.tint) ? o.tint[Math.floor(Math.random() * o.tint.length)] : (o.tint ?? 0xffffff);
      s.visible = true;
      const ang = o.angle === undefined ? Math.random() * Math.PI * 2 : o.angle + rand(-(o.spread ?? Math.PI), o.spread ?? Math.PI);
      const sp = rand(o.speed[0], o.speed[1]);
      this.container.addChild(s);
      this.live.push({
        sprite: s,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp,
        vr: o.rotationSpeed ? rand(-o.rotationSpeed, o.rotationSpeed) : 0,
        life: rand(o.life[0], o.life[1]),
        age: 0,
        gravity: o.gravity ?? 0,
        fade: o.fade ?? true,
        bounceY: o.bounceY,
        bounced: false,
        bounceDamping: o.bounceDamping ?? 0.45,
        frames: o.frames && o.frames.length > 1 ? o.frames : undefined,
        frameMs: o.frameMs ?? 60,
      });
    }
  }

  private kill(p: Particle): void {
    const i = this.live.indexOf(p);
    if (i >= 0) this.live.splice(i, 1);
    p.sprite.visible = false;
    this.container.removeChild(p.sprite);
    this.pool.push(p.sprite);
  }

  update(dt: number): void {
    const s = dt / 1000;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i];
      p.age += dt;
      if (p.age >= p.life) {
        this.kill(p);
        continue;
      }
      p.vy += p.gravity * s;
      p.sprite.x += p.vx * s;
      p.sprite.y += p.vy * s;
      p.sprite.rotation += p.vr * s;
      if (p.frames) p.sprite.texture = p.frames[Math.floor(p.age / p.frameMs) % p.frames.length];
      if (p.bounceY !== undefined && !p.bounced && p.vy > 0 && p.sprite.y >= p.bounceY) {
        p.sprite.y = p.bounceY;
        p.vy = -p.vy * p.bounceDamping;
        p.vx *= 0.7;
        p.bounced = true;
      }
      if (p.fade) {
        const k = p.age / p.life;
        if (k > 0.6) p.sprite.alpha = 1 - (k - 0.6) / 0.4;
      }
    }
  }

  clear(): void {
    for (const p of this.live.slice()) this.kill(p);
  }
}

function rand(a: number, b: number): number {
  return a + Math.random() * (b - a);
}
